import {Dropdown, Tooltip} from 'platform/components';
import {Center, Icon, Text} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';
import {useFocusControl} from '@dms/shared';

import {useBoolean} from 'shared/web';

import {HeaderButtonWrapper} from '../HeaderButtonWrapper';
import {TopBarActionContainer} from '../TopBarActionContainer';
import {AvailabilityStatus} from './AvailabilityStatus';
import {AvailabilityStatusDot} from './AvailabilityStatusDot';
import {AvailabilityStatusMenu} from './AvailabilityStatusMenu';
import {useAvailabilityStatus} from './useAvailabilityStatus';

export function AvailabilityStatusSelect() {
  const [isOpen, open, close] = useBoolean(false);
  const {isDisabled} = useFocusControl();
  const {status} = useAvailabilityStatus();

  const statusLabel = match<AvailabilityStatus, string>(status)
    .with('available', () => i18n.t('user.awayStatus.available'))
    .with('away', () => i18n.t('user.awayStatus.away'))
    .with('absent', () => i18n.t('user.awayStatus.absent'))
    .exhaustive();

  const arrowIcon = match(isOpen)
    .with(true, () => 'navigation/arrow_drop_up' as const)
    .with(false, () => 'navigation/arrow_drop_down' as const)
    .exhaustive();

  return (
    <Dropdown
      data-testid="availabilityStatusSelect-dropdown"
      isOpen={isOpen}
      onOpen={open}
      onClose={close}
      placement="bottom-start"
      isLazy
      lazyBehavior="unmount"
      dropdownControl={
        <TopBarActionContainer>
          <Center height={12}>
            <HeaderButtonWrapper $isOpen={isOpen}>
              <Tooltip
                label={i18n.t('user.awayStatus.tooltip')}
                isDisabled={status === 'available'}
              >
                <StyledTrigger
                  type="button"
                  aria-label="topBar availabilityStatus"
                  tabIndex={isDisabled ? -1 : 0}
                  data-testid="availabilityStatusSelect"
                >
                  <AvailabilityStatusDot status={status} />
                  <Text size="small">{statusLabel}</Text>
                  <Icon value={arrowIcon} size={4} />
                </StyledTrigger>
              </Tooltip>
            </HeaderButtonWrapper>
          </Center>
        </TopBarActionContainer>
      }
    >
      <AvailabilityStatusMenu onClose={close} />
    </Dropdown>
  );
}

const StyledTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  background: transparent;
  border: none;
  cursor: pointer;
  height: ${({theme}) => theme.getSize(8)};
  padding-block: ${({theme}) => theme.getSize(1)};
  padding-inline: ${({theme}) => theme.getSize(3)};
  border-radius: ${({theme}) => theme.getSize(1)};
  white-space: nowrap;
  transition: all 250ms;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({theme}) => theme.getSize(1)}
      ${({theme}) => theme.colors.palettes.blue[40][100]};
  }
`;
